import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ProcedureMoveAppBar from "../../../common/ProcedureMoveAppBar";
import Snackbar from "../../../common/Snackbar";
import { copyElement } from "../../../redux/sectionSlice";
import { generateSHA256 } from "../../../util/generateSHA256";
import { getPages, renderPreview, renderVideo } from "../plugins/utils";
import {
  applyTransparency,
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  measureHtmlHeight,
  stripHtmlPreserveFormatting,
} from "../../SectionElements/plugins/utils.js";
import RectangleElement from "../../Canvas/components/rectangle/RectangleElement.jsx";
import ArrowElement from "../../Canvas/components/arrow/ArrowElement.jsx";
import CircleElement from "../../Canvas/components/circle/CircleElement.jsx";
import LineElement from "../../Canvas/components/line/LineElement.jsx";
import CanvasImage from "../../Canvas/components/canvas-core/CanvasImage.jsx";
import TextElement from "../../Canvas/components/text/TextElement.jsx";
import DoubleSidedArrowElement from "../../Canvas/components/arrow/DoubleSidedArrowElement.jsx";
import CanvasDisplay from "../../Canvas/components/canvas-core/CanvasDisplay.jsx";
import {
  generateSourceTreePath,
  generateTargetTreePath,
  getDisplayIndex,
  getSubstepLetter,
  generateStatusText,
  validateAndCorrectTreePathData,
  generateCompleteTreePath,
} from "../../SubassemblyDetails/utils/treePathUtils";
import RenderHtml from "../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
const ProcedureChildCopyPreview = () => {
  const {
    stepsBySection,
    substepsByStep,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
  } = useSelector((state) => state.step);
  const { sections } = useSelector((state) => state.section);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Destructure location.state first to get all required variables
  const {
    moveType,
    sourceSectionIndex,
    targetSectionIndex,
    revision,
    partNumber,
    type,
    partId,
    revisionId,
    fromTreeId,
    fromSectionId,
    targetSectionData,
    fromSiblingOrder,
    node,
    nodeType = "richText", // Provide default value
    fromNodeId,
    targetNodeType,
    fromSubstepId,
    sourceStepId,
    fromStepId, // Add fromStepId to destructuring
    fromPartId,
    fromRevisionId,
    title,
    status,
    toPartNumber,
    toType,
    description,
    treeNode,
    childTreeId,
    fromSubstepSiblingOrder,
  } = location.state || {};

  const [newVideos, setVideos] = useState({});
  const [newRichTexts, setRichTexts] = useState({});
  const [newAttachments, setAttachments] = useState({});
  const [newForms, setForms] = useState({});
  const [newCanvases, setCanvases] = useState({});

  // Dedicated state for pasted elements that persists across view changes
  const [pastedElements, setPastedElements] = useState({
    richTexts: {},
    attachments: {},
    forms: {},
    canvases: {},
    videos: {},
  });

  const [substepsByStepPreview, setSubstepsByStepPreview] = useState({});
  let richTextCounter = 1;
  let canvasCounter = 1;
  let videoCounter = 1;
  let formCounter = 1;
  let substepCounter = 1;
  let stepCounter = 1;
  const [isLoading, setIsLoading] = useState(false);
  const [auditData, setAuditData] = useState({
    user_ref: localStorage.getItem("user_id") || "",
    ip_address: "",
    device_info: navigator.userAgent,
    hash: generateSHA256(Math.random().toString()),
    signature: "",
  });
  const [pastingCompleted, setPastingCompleted] = useState(false);
  let richTextsCounter = {};
  let attachmentCounter = 1;
  const [isModalOpen, setModalOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const [stepsBySectionPreview, setStepsBySectionPreview] = useState({});

  const [selectedView, setSelectedView] = useState("new");

  // Enhanced view switching function
  const handleViewChange = (newView) => {
    setSelectedView(newView);

    // Reset all state when switching views
    if (newView === "original") {
      // For "View Original", show only original content (hide pasted elements)

      // Use clean Redux state to hide pasted elements
      setRichTexts({ ...richTexts });
      setAttachments({ ...attachments });
      setForms({ ...forms });
      setCanvases({ ...canvases });
      setVideos({ ...videos });
      setSubstepsByStepPreview({ ...substepsByStep });
      setStepsBySectionPreview({ ...stepsBySection });

      // Reset pasting state
      pastedNodeIdRef.current = null;
      setPastingCompleted(false);
    } else if (newView === "new") {
      // For "View New", show both existing content and pasted elements

      // Merge Redux state with pasted elements to show both
      // But only add pasted elements that aren't already in the main state
      const mergedRichTexts = { ...richTexts };
      const mergedAttachments = { ...attachments };
      const mergedForms = { ...forms };
      const mergedCanvases = { ...canvases };
      const mergedVideos = { ...videos };

      // Only add pasted elements if they're not already in the main state
      Object.keys(pastedElements.richTexts).forEach((key) => {
        if (!mergedRichTexts[key]) {
          mergedRichTexts[key] = pastedElements.richTexts[key];
        }
      });
      Object.keys(pastedElements.attachments).forEach((key) => {
        if (!mergedAttachments[key]) {
          mergedAttachments[key] = pastedElements.attachments[key];
        }
      });
      Object.keys(pastedElements.forms).forEach((key) => {
        if (!mergedForms[key]) {
          mergedForms[key] = pastedElements.forms[key];
        }
      });
      Object.keys(pastedElements.canvases).forEach((key) => {
        if (!mergedCanvases[key]) {
          mergedCanvases[key] = pastedElements.canvases[key];
        }
      });
      Object.keys(pastedElements.videos).forEach((key) => {
        if (!mergedVideos[key]) {
          mergedVideos[key] = pastedElements.videos[key];
        }
      });

      setRichTexts(mergedRichTexts);
      setAttachments(mergedAttachments);
      setForms(mergedForms);
      setCanvases(mergedCanvases);
      setVideos(mergedVideos);
      setSubstepsByStepPreview({ ...substepsByStep });
      setStepsBySectionPreview({ ...stepsBySection });

      // Reset pasting state
      pastedNodeIdRef.current = null;
      setPastingCompleted(false);
    }
  };

  // Initialize state only once when component mounts
  useEffect(() => {
    if (selectedView === "new" && Object.keys(newRichTexts).length === 0) {
      // Initialize with current Redux state to preserve existing content
      // But don't duplicate pasted elements that are already in pastedElements
      setRichTexts({ ...richTexts });
      setAttachments({ ...attachments });
      setForms({ ...forms });
      setCanvases({ ...canvases });
      setVideos({ ...videos });

      // Also initialize substepsByStepPreview if we're copying a substep
      if (nodeType === "substep" && fromSubstepId) {
        setSubstepsByStepPreview((prev) => ({
          ...prev,
          [sourceStepId]: substepsByStep[sourceStepId] || [],
        }));
      }

      // Initialize stepsBySectionPreview with the current steps
      setStepsBySectionPreview({ ...stepsBySection });
    }
  }, [
    selectedView,
    nodeType,
    fromSubstepId,
    sourceStepId,
    substepsByStep,
    stepsBySection,
  ]);

  // Remove the problematic useEffect that was overwriting state
  // This useEffect was causing newly pasted elements to vanish

  const pastedNodeIdRef = useRef(null);

  const targetStepId = targetSectionData?.stepId;
  const targetSubstepId = targetSectionData?.substepId;
  const targetSiblingNodeId = targetSectionData?.elementId;
  const [previewSections, setPreviewSections] = useState([...sections]);
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const getNodeType = (node) => {
    if (!node || typeof node !== "object") return "richText";

    // Check specific ID properties first to determine type (most reliable)
    if ("attachment_tree_node_id" in node) return "attachment";
    if ("rich_text_tree_node_id" in node || "rich_text_node_id" in node)
      return "richText";
    if ("form_tree_node_id" in node) return "form";
    if ("canvas_tree_node_id" in node) return "canvas";
    if ("video_tree_node_id" in node) return "video";

    // Fallback to type/content_type if ID properties are not present
    const type = node.type || node.content_type || "";
    const normalized = type.toLowerCase().replace(/\s+/g, "");

    switch (normalized) {
      case "richtext":
      case "rich-text": // Handle "rich-text" with hyphen
        return "richText";
      case "form":
      case "attachment":
      case "canvas":
      case "video":
        return normalized;
      default:
        return "richText"; // final fallback
    }
  };

  useEffect(() => {
    if (selectedView === "original") {
      // Use original Redux state
      setRichTexts(richTexts);
      setAttachments(attachments);
      setForms(forms);
      setCanvases(canvases);
      setVideos(videos);
      setSubstepsByStepPreview(substepsByStep);
      setStepsBySectionPreview(stepsBySection);
      return;
    }

    // For "new" view, use our local modified state
  }, [
    selectedView,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
    substepsByStep,
    stepsBySection,
  ]);

  // Process the node data and integrate it into the component's state
  useEffect(() => {
    if (node && selectedView === "new") {

      // For copying, we need to place the element in the target step that the user selected
      // The targetStepId should come from the target procedure, not the source
      // Since this is a copy preview, we'll use a temporary step ID for now
      // The actual placement will happen when the user confirms the copy
      const tempTargetStepId = "temp-copy-step";

      if (tempTargetStepId) {
        if (node.elementType === "attachment" || node.type === "attachment") {
          // Store only in pasted elements state to avoid duplication
          setPastedElements((prev) => ({
            ...prev,
            attachments: {
              ...prev.attachments,
              [tempTargetStepId]: [node.data || node.attachments || node],
            },
          }));
        } else if (
          node.elementType === "richText" ||
          node.type === "richText"
        ) {
          setPastedElements((prev) => ({
            ...prev,
            richTexts: {
              ...prev.richTexts,
              [tempTargetStepId]: [node.data || node.richTexts || node],
            },
          }));
        } else if (node.elementType === "video" || node.type === "video") {
          setPastedElements((prev) => ({
            ...prev,
            videos: {
              ...prev.videos,
              [tempTargetStepId]: [node.data || node.videos || node],
            },
          }));
        } else if (node.elementType === "canvas" || node.type === "canvas") {
          setPastedElements((prev) => ({
            ...prev,
            canvases: {
              ...prev.canvases,
              [tempTargetStepId]: [node.data || node.canvases || node],
            },
          }));
        } else if (node.elementType === "form" || node.type === "form") {
          setPastedElements((prev) => ({
            ...prev,
            forms: {
              ...prev.forms,
              [tempTargetStepId]: [node.data || node.forms || node],
            },
          }));
        }
      }

      // Debug: Log the state after processing
      setTimeout(() => {
      }, 100);
    }
  }, [node, selectedView]);

  // The pasting logic below will handle updating these states
  useEffect(() => {
    // Reset the pasting state when switching to "new" view
    if (selectedView === "new") {
      pastedNodeIdRef.current = null;
      setPastingCompleted(false);
    }

    // Guard against multiple executions only within the same view
    if (pastedNodeIdRef.current || pastingCompleted) {
      return;
    }

    // Use the local state targetSectionData instead of location.state
    if (!targetSectionData) {
      return;
    }

    const targetElementId = targetSectionData?.elementId;
    const targetSiblingOrder = targetSectionData?.elementSiblingOrder;
    const maxSiblingOrder = targetSectionData?.maxSiblingOrder;

    if (!node) {
      return;
    }

    const clonedNode = {
      ...structuredClone(node),
      ...node.data,
    };


    // Handle substep association based on target context
    if (targetSubstepId) {
      // If we're pasting into a substep, ensure the cloned node is associated with that substep
      clonedNode.sub_step_tree_node_id = targetSubstepId;
    } else if (clonedNode.sub_step_tree_node_id) {
      // If we're pasting into a step (not substep), clear any existing substep association
      clonedNode.sub_step_tree_node_id = null;
    }

    const uniqueNodeId = getNodeId(clonedNode);

    if (pastedNodeIdRef.current === uniqueNodeId) return;

    const parentId = targetSubstepId || targetStepId;

    if (!parentId) {
      return;
    }

    // If we're copying a substep, we need to handle its child nodes differently
    if (nodeType === "substep") {
      // Get the original substep's child nodes from the source
      const sourceParentId = fromSubstepId || sourceStepId;
      const sourceChildren = [
        ...(richTexts[sourceParentId] || []),
        ...(forms[sourceParentId] || []),
        ...(attachments[sourceParentId] || []),
        ...(canvases[sourceParentId] || []),
        ...(videos[sourceParentId] || []),
      ];

      // Sort source children by their original sibling_order to maintain sequence
      sourceChildren.sort((a, b) => {
        const orderA = parseInt(a.sibling_order || 0);
        const orderB = parseInt(b.sibling_order || 0);
        return orderA - orderB;
      });


      // Get existing children in the target location
      const targetChildren = [
        ...(richTexts[parentId] || []),
        ...(forms[parentId] || []),
        ...(attachments[parentId] || []),
        ...(canvases[parentId] || []),
        ...(videos[parentId] || []),
      ];

      // Sort target children by sibling_order
      targetChildren.sort((a, b) => {
        const orderA = parseInt(a.sibling_order || 0);
        const orderB = parseInt(b.sibling_order || 0);
        return orderA - orderB;
      });


      // Find the target element index for positioning
      const targetIndex = targetChildren.findIndex(
        (item) => getNodeId(item) === targetElementId
      );

      // Determine insertion position based on moveType
      let insertIndex = targetChildren.length; // Default to end

      if (targetElementId && targetIndex !== -1) {
        if (moveType === "up") {
          insertIndex = targetIndex;
        } else if (moveType === "below") {
          insertIndex = targetIndex + 1;
        }
      }


      // Create a new array with the substep and its children inserted at the correct position
      const newTargetChildren = [...targetChildren];

      // First, insert the substep itself
      const clonedSubstep = {
        ...clonedNode,
        sibling_order: insertIndex + 1, // Temporary order, will be recalculated
      };

      // Ensure the cloned substep has a proper ID
      if (!clonedSubstep.id && !clonedSubstep.sub_step_tree_node_id) {
        // Generate a temporary ID for the cloned substep
        clonedSubstep.id = `temp_substep_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        clonedSubstep.sub_step_tree_node_id = clonedSubstep.id;
      }


      newTargetChildren.splice(insertIndex, 0, clonedSubstep);

      // Then, insert all child nodes of the substep after the substep
      let childInsertIndex = insertIndex + 1;
      sourceChildren.forEach((child, childIndex) => {
        const clonedChild = {
          ...structuredClone(child),
          sibling_order: childInsertIndex + 1, // Temporary order, will be recalculated
        };

        // Update the child's substep association to point to the new substep
        // Use the cloned substep's ID or tree node ID
        if (clonedChild.sub_step_tree_node_id) {
          // For substep copying, we need to ensure the child nodes are properly associated
          // with the new substep. The substep ID should be the one from the target location
          clonedChild.sub_step_tree_node_id =
            targetSubstepId ||
            clonedSubstep.id ||
            clonedSubstep.sub_step_tree_node_id;
        }

        // Also update any other substep references
        if (clonedChild.substep_id_ref) {
          clonedChild.substep_id_ref =
            targetSubstepId ||
            clonedSubstep.id ||
            clonedSubstep.sub_step_tree_node_id;
        }

        // Update the substep_id_ref if it exists
        if (clonedChild.substep_id_ref) {
          clonedChild.substep_id_ref =
            targetSubstepId ||
            clonedSubstep.id ||
            clonedSubstep.sub_step_tree_node_id;
        }


        newTargetChildren.splice(childInsertIndex, 0, clonedChild);
        childInsertIndex++;
      });

      // Now reassign sequential sibling_order values (1, 2, 3, 4...)
      newTargetChildren.forEach((item, index) => {
        item.sibling_order = index + 1;
      });


      // Separate by type while maintaining the order
      const orderedItems = newTargetChildren.map((item) => ({
        ...item,
        type: getNodeType(item),
      }));

      // Update pastedElements state with the ordered items, maintaining the sequence
      const canvasItems = orderedItems.filter((item) => item.type === "canvas");

      setPastedElements((prev) => ({
        ...prev,
        richTexts: {
          ...prev.richTexts,
          [parentId]: orderedItems.filter((item) => item.type === "richText"),
        },
        attachments: {
          ...prev.attachments,
          [parentId]: orderedItems.filter((item) => item.type === "attachment"),
        },
        forms: {
          ...prev.forms,
          [parentId]: orderedItems.filter((item) => item.type === "form"),
        },
        canvases: {
          ...prev.canvases,
          [parentId]: canvasItems,
        },
        videos: {
          ...prev.videos,
          [parentId]: orderedItems.filter((item) => item.type === "video"),
        },
      }));

      // For substep copying, we also need to store the child nodes under the new substep ID
      // so they appear correctly in the preview
      if (nodeType === "substep") {
        const newSubstepId =
          clonedSubstep.id || clonedSubstep.sub_step_tree_node_id;


        // Get the child nodes that belong to this substep
        // Since we're copying a substep, all the child nodes we just inserted should belong to it
        const substepChildren = orderedItems.filter((item) => {
          // Skip the substep itself
          if (getNodeId(item) === getNodeId(clonedSubstep)) {
            return false;
          }

          // Include all child nodes that were part of the original substep
          // These should be the ones we just inserted after the substep
          return true;
        });


        // Store them under the new substep ID in the pastedElements state
        setPastedElements((prev) => ({
          ...prev,
          richTexts: {
            ...prev.richTexts,
            [newSubstepId]: substepChildren.filter(
              (item) => item.type === "richText"
            ),
          },
          attachments: {
            ...prev.attachments,
            [newSubstepId]: substepChildren.filter(
              (item) => item.type === "attachment"
            ),
          },
          forms: {
            ...prev.forms,
            [newSubstepId]: substepChildren.filter(
              (item) => item.type === "form"
            ),
          },
          canvases: {
            ...prev.canvases,
            [newSubstepId]: substepChildren.filter(
              (item) => item.type === "canvas"
            ),
          },
          videos: {
            ...prev.videos,
            [newSubstepId]: substepChildren.filter(
              (item) => item.type === "video"
            ),
          },
        }));

      }

      // Also update the substepsByStepPreview state to show the copied substep
      if (nodeType === "substep") {
        setSubstepsByStepPreview((prev) => ({
          ...prev,
          [targetStepId]: [
            ...(prev[targetStepId] || []),
            {
              ...clonedSubstep,
              sibling_order: insertIndex + 1,
            },
          ].sort(
            (a, b) =>
              parseInt(a.sibling_order || 0) - parseInt(b.sibling_order || 0)
          ),
        }));

        // Also update the stepsBySectionPreview state to ensure the step appears in the section
        setStepsBySectionPreview((prev) => {
          const sectionId = targetSectionData?.sectionId;
          if (sectionId) {
            const currentSteps = prev[sectionId] || [];
            const stepExists = currentSteps.find(
              (step) => step.id === targetStepId
            );

            if (!stepExists) {
              // Add the step to the section if it doesn't exist
              return {
                ...prev,
                [sectionId]: [...currentSteps, { id: targetStepId }],
              };
            }
          }
          return prev;
        });
      }

    } else {
      // Handle regular element copying (non-substep)
      // Get all existing children for the parent - use the actual displayed order
      const childrenMap = {
        richText: richTexts[parentId] || [],
        form: forms[parentId] || [],
        attachment: attachments[parentId] || [],
        canvas: canvases[parentId] || [],
        video: videos[parentId] || [],
      };

      // Combine all children in the order they appear on screen
      const allChildren = [
        ...childrenMap.richText,
        ...childrenMap.form,
        ...childrenMap.attachment,
        ...childrenMap.canvas,
        ...childrenMap.video,
      ];

      // Sort by sibling_order to get the correct display order
      allChildren.sort((a, b) => {
        const orderA = parseInt(a.sibling_order || 0);
        const orderB = parseInt(b.sibling_order || 0);
        return orderA - orderB;
      });

      // Create a deep copy of all children to avoid modifying read-only objects
      const allChildrenCopy = allChildren.map((item) => ({
        ...item,
        sibling_order: parseInt(item.sibling_order || 0),
      }));

      // Find the target element index
      const targetIndex = allChildrenCopy.findIndex(
        (item) => getNodeId(item) === targetElementId
      );

      // Determine insertion position based on moveType and target element
      let insertIndex = allChildrenCopy.length; // Default to end

      if (targetElementId && targetIndex !== -1) {
        if (moveType === "up") {
          // Insert before the target element
          if (targetIndex === 0) {
            // If target is at the beginning, insert at the very beginning
            insertIndex = 0;
          } else {
            // Insert before the target element (at targetIndex, since array is 0-indexed)
            insertIndex = targetIndex;
          }
        } else if (moveType === "below") {
          // Insert after the target element (at targetIndex + 1, since array is 0-indexed)
          insertIndex = targetIndex + 1;
        }
      } else if (moveType === "into") {
        // When pasting "into" a step/substep (not relative to another element),
        // always place at the end
        insertIndex = allChildrenCopy.length;
      } else {
        // For other cases (up/below with no target), default to end
        insertIndex = allChildrenCopy.length;
      }

      // Set a temporary sibling_order for the cloned node (will be reassigned later)
      clonedNode.sibling_order = 999; // Temporary high value

      // Insert the cloned node at the correct position
      const newAllChildren = [...allChildrenCopy];
      newAllChildren.splice(insertIndex, 0, clonedNode);

      // Now reassign sequential sibling_order values (1, 2, 3, 4...)
      newAllChildren.forEach((item, index) => {
        item.sibling_order = index + 1;
      });

      // Instead of separating by type and losing order, use the ordered array directly
      // This preserves the exact sequence we calculated
      const orderedItems = newAllChildren.map((item) => ({
        ...item,
        type: getNodeType(item),
      }));

      // Update state with the ordered items, maintaining the sequence
      const canvasItems = orderedItems.filter((item) => item.type === "canvas");

      setRichTexts((prev) => ({
        ...prev,
        [parentId]: orderedItems.filter((item) => item.type === "richText"),
      }));
      setAttachments((prev) => ({
        ...prev,
        [parentId]: orderedItems.filter((item) => item.type === "attachment"),
      }));
      setForms((prev) => ({
        ...prev,
        [parentId]: orderedItems.filter((item) => item.type === "form"),
      }));
      setCanvases((prev) => {
        const newState = {
          ...prev,
          [parentId]: canvasItems,
        };
        return newState;
      });
      setVideos((prev) => ({
        ...prev,
        [parentId]: orderedItems.filter((item) => item.type === "video"),
      }));
    }

    pastedNodeIdRef.current = uniqueNodeId;
    setPastingCompleted(true);
  }, [
    node,
    targetSubstepId,
    targetStepId,
    targetSectionData,
    moveType,
    selectedView,
    nodeType,
    fromSubstepId,
    sourceStepId,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
    setRichTexts,
    setAttachments,
    setForms,
    setCanvases,
    setVideos,
    setPastedElements,
  ]);

  const getNodeId = (item) =>
    item.id ||
    item.rich_text_tree_node_id ||
    item.rich_text_node_id ||
    item.attachment_tree_node_id ||
    item.form_tree_node_id ||
    item.canvas_tree_node_id ||
    item.video_tree_node_id;

  // Re-added relevant dependencies to useEffect to ensure it responds to changes
  // and removed the exhaustive-deps eslint-disable comment.

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleConfirm = () => {
    setModalOpen(true);
  };

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };
  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) =>
        setAuditData((prev) => ({ ...prev, ip_address: data.ip }))
      )
      .catch((err) => {});
  }, []);

  const handleConfirmSave = async () => {
    if (!comment) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }

    // Validate required variables
    if (!partId || !revisionId || !toType || !toPartNumber) {
      const missingVars = [];
      if (!partId) missingVars.push("partId");
      if (!revisionId) missingVars.push("revisionId");
      if (!toType) missingVars.push("toType");
      if (!toPartNumber) missingVars.push("toPartNumber");

      showSnackbar(
        `Missing required variables: ${missingVars.join(", ")}`,
        "error"
      );
      return;
    }

    // Additional safety checks for critical variables
    if (!nodeType || !moveType || !targetSectionData) {
      showSnackbar(
        "Missing critical configuration data. Please try again.",
        "error"
      );
      return;
    }

    setIsLoading(true);

    const hash = await generateSHA256(Math.random().toString());

    const from_type =
      nodeType === "richText"
        ? "rich-text"
        : nodeType === "canvas"
        ? "canva"
        : (nodeType || "richText").toLowerCase();

    let to_type =
      targetNodeType?.toLowerCase() === "rich text"
        ? "rich-text"
        : targetNodeType?.toLowerCase()?.replace(/\s+/g, "_") || "rich-text";

    // Ensure to_type is not "step" or "substep"
    if (to_type === "step" || to_type === "substep") {
      to_type = from_type || "rich-text";
    }

    let toOrder = parseInt(
      targetSectionData?.stepSiblingOrder ||
        targetSectionData?.substepSiblingOrder ||
        "1"
    );
    let elementSiblingOrder = parseInt(
      targetSectionData?.elementSiblingOrder || "1"
    );

    // Fix the toOrder calculation for proper positioning
    if (moveType === "up" || moveType === "below") {
      // For both "paste above" and "paste below", we need to find the target element's actual position
      const targetElementId = targetSectionData?.elementId;
      if (targetElementId) {
        // Determine the correct parent ID to get children from
        const parentId = targetSubstepId || targetStepId;

        // Find the target element in the current list to get its actual position
        const allChildren = [
          ...(richTexts[parentId] || []),
          ...(forms[parentId] || []),
          ...(attachments[parentId] || []),
          ...(canvases[parentId] || []),
          ...(videos[parentId] || []),
        ];

        // Sort by sibling order to get the correct position
        allChildren.sort((a, b) => {
          const orderA = parseInt(a.sibling_order || 0);
          const orderB = parseInt(b.sibling_order || 0);
          return orderA - orderB;
        });

        const targetIndex = allChildren.findIndex(
          (item) => getNodeId(item) === targetElementId
        );

        if (targetIndex !== -1) {
          if (moveType === "up") {
            // For "paste above", use the target element's current position (1-based)
            toOrder = targetIndex + 1;
          } else if (moveType === "below") {
            // For "paste below", place after the target element
            toOrder = targetIndex + 2; // +2 because we want to place after the target (targetIndex + 1 + 1)
          }
        } else {
          // Fallback to elementSiblingOrder if we can't find the target
          if (moveType === "up") {
            toOrder = elementSiblingOrder;
          } else if (moveType === "below") {
            toOrder = elementSiblingOrder + 1;
          }
        }
      } else {
        // No target element specified, fallback to elementSiblingOrder
        if (moveType === "up") {
          toOrder = elementSiblingOrder;
        } else if (moveType === "below") {
          toOrder = elementSiblingOrder + 1;
        }
      }
    } else if (moveType === "into") {
      // For "paste into", place at the end of the step/substep
      // Determine the correct parent ID to get children from
      const parentId = targetSubstepId || targetStepId;

      // Get all existing children and find the highest sibling order
      const allChildren = [
        ...(richTexts[parentId] || []),
        ...(forms[parentId] || []),
        ...(attachments[parentId] || []),
        ...(canvases[parentId] || []),
        ...(videos[parentId] || []),
      ];

      // Find the highest sibling order
      const maxOrder = Math.max(
        ...allChildren.map((item) => parseInt(item.sibling_order || 0)),
        0
      );

      // Place after the last element
      toOrder = maxOrder + 1;
    } else {
      // For other move types, use the calculated order
      toOrder = parseInt(
        targetSectionData?.stepSiblingOrder ||
          targetSectionData?.substepSiblingOrder ||
          "1"
      );
    }

    // Calculate the source element's position in its original location
    const calculateFromOrder = () => {
      // If we have the source step ID, find the element's position there
      if (sourceStepId) {
        const sourceParentId = fromSubstepId || sourceStepId;
        const sourceChildren = [
          ...(richTexts[sourceParentId] || []),
          ...(forms[sourceParentId] || []),
          ...(attachments[sourceParentId] || []),
          ...(canvases[sourceParentId] || []),
          ...(videos[sourceParentId] || []),
        ];

        // Sort by sibling order to get the correct position
        sourceChildren.sort((a, b) => {
          const orderA = parseInt(a.sibling_order || 0);
          const orderB = parseInt(b.sibling_order || 0);
          return orderA - orderB;
        });

        const sourceIndex = sourceChildren.findIndex(
          (item) => getNodeId(item) === (node ? getNodeId(node) : "")
        );
        if (sourceIndex !== -1) {
          return sourceIndex + 1; // Convert to 1-based index
        } else {
        }
      }

      // Fallback to the passed value or 1
      const fallbackOrder = parseInt(fromSubstepSiblingOrder || "1") || 1;
      return fallbackOrder;
    };

    // For substep copying, we need to ensure the toOrder is calculated correctly
    // considering that the substep and its children will be inserted as a group
    if (nodeType === "substep") {
      // When copying a substep, we need to find the correct position
      // where the substep should be inserted
      const parentId = targetSubstepId || targetStepId;

      if (moveType === "into") {
        // For "paste into", find the highest order in the target step/substep
        const allChildren = [
          ...(richTexts[parentId] || []),
          ...(forms[parentId] || []),
          ...(attachments[parentId] || []),
          ...(canvases[parentId] || []),
          ...(videos[parentId] || []),
        ];

        const maxOrder = Math.max(
          ...allChildren.map((item) => parseInt(item.sibling_order || 0)),
          0
        );

        toOrder = maxOrder + 1;
      } else if (moveType === "up" || moveType === "below") {
        // For relative positioning, use the existing logic
        // but ensure we're positioning the substep correctly
        const targetElementId = targetSectionData?.elementId;
        if (targetElementId) {
          const allChildren = [
            ...(richTexts[parentId] || []),
            ...(forms[parentId] || []),
            ...(attachments[parentId] || []),
            ...(canvases[parentId] || []),
            ...(videos[parentId] || []),
          ];

          allChildren.sort((a, b) => {
            const orderA = parseInt(a.sibling_order || 0);
            const orderB = parseInt(b.sibling_order || 0);
            return orderA - orderB;
          });

          const targetIndex = allChildren.findIndex(
            (item) => getNodeId(item) === targetElementId
          );

          if (targetIndex !== -1) {
            if (moveType === "up") {
              toOrder = targetIndex + 1;
            } else if (moveType === "below") {
              toOrder = targetIndex + 2;
            }
          }
        }
      }
    }

    const payload = {
      from_procedure_tree_node_ref: fromTreeId,
      from_order: calculateFromOrder(),
      from_type:
        nodeType === "richText"
          ? "rich-text"
          : nodeType === "canvas"
          ? "canva"
          : nodeType || "rich-text",
      from_part_id_ref: fromPartId,
      from_procedure_rev_ref: fromRevisionId,
      from_section_id_ref: fromSectionId,
      from_step_id_ref: sourceStepId,
      from_child_node_ref: node ? getNodeId(node) : "",
      from_substep_id_ref:
        fromSubstepId && fromSubstepId !== "null" ? fromSubstepId : "",
      to_procedure_tree_node_ref: targetSectionData?.sectionTreeId,
      to_order: toOrder,
      to_type: (() => {
        // When pasting above/below an element, the to_type should be the type of the target element
        if (moveType === "up" || moveType === "below") {
          // Get the target element to determine its type
          const targetElementId = targetSectionData?.elementId;
          if (targetElementId) {
            const parentId = targetSubstepId || targetStepId;
            const allChildren = [
              ...(richTexts[parentId] || []),
              ...(forms[parentId] || []),
              ...(attachments[parentId] || []),
              ...(canvases[parentId] || []),
              ...(videos[parentId] || []),
            ];

            const targetElement = allChildren.find(
              (item) => getNodeId(item) === targetElementId
            );
            if (targetElement) {
              const targetElementType = getNodeType(targetElement);
              // Convert to the correct format for the API
              if (targetElementType === "richText") return "rich-text";
              if (targetElementType === "canvas") return "canva";
              return targetElementType;
            }
          }
        }

        // For other cases, use the calculated to_type
        if (
          (to_type && to_type.toLowerCase() === "substep") ||
          (to_type && to_type.toLowerCase() === "step")
        ) {
          return nodeType === "rich-text"
            ? "rich-text"
            : nodeType === "canvas"
            ? "canva"
            : nodeType || "rich-text";
        }
        return to_type || "rich-text";
      })(),

      to_part_id_ref: partId,
      to_procedure_rev_ref: revisionId,
      to_section_id_ref: targetSectionData?.sectionId,
      to_step_id_ref: targetSectionData.stepId,
      to_substep_id_ref: targetSectionData.substepId
        ? targetSectionData.substepId
        : "",
      direction:
        moveType === "up"
          ? "paste_up"
          : moveType === "below"
          ? "paste_down"
          : "paste_into",
      audit_trail_data: {
        ...auditData,
        hash,
        action:
          moveType === "up"
            ? "paste_up"
            : moveType === "below"
            ? "paste_down"
            : "paste_into",
        comment: `Copied Child`,
        date: new Date().toISOString(),
        parent_version: null,
        version_content: {
          rule_name: "Field Changes Tracking",
          modified_fields: {},
        },
      },
    };

    try {
      // Check if dispatch function is available
      if (!dispatch) {
        throw new Error("Dispatch function not available");
      }

      // Check if copyElement action is available
      if (typeof copyElement !== "function") {
        throw new Error("copyElement action not available");
      }

      const result = await dispatch(copyElement(payload)).unwrap();

      // Close modal and stop loading
      setModalOpen(false);
      setIsLoading(false);

      // Clear any error states
      setSnackbar({ open: false, message: "", type: "" });

      // Navigate to revision submission page
      const navigationPath = `/revision-submission/${partId || "unknown"}/${
        revision || "unknown"
      }/${toType || "rich-text"}/${toPartNumber || "unknown"}/${
        revisionId || "unknown"
      }/${encodeURIComponent(description || "No description")}`;

      // Add a small delay to ensure state updates are processed
      setTimeout(() => {
        try {
          if (navigate && typeof navigate === "function") {
            navigate(navigationPath, {
              state: {
                status: status || location.state?.status || "draft",
                fromCopy: true,
                copiedNode: node || {},
              },
              replace: true,
            });
          } else {
            window.location.href = navigationPath;
          }
        } catch (navError) {
          // Fallback navigation
          window.location.href = navigationPath;
        }
      }, 100);
    } catch (error) {
      setIsLoading(false);

      // Show more detailed error message
      const errorMessage =
        error?.message || error?.error || "Unknown error occurred";
      showSnackbar(
        `Error copying ${nodeType || "element"}: ${errorMessage}`,
        "error"
      );

      // Keep modal open on error so user can try again
      setModalOpen(true);
    }
  };
  const getReorderedSections = (
    sections,
    sourceIndex,
    targetIndex,
    moveType
  ) => {
    return sections;
  };

  const reorderedPreviewSections = getReorderedSections(
    [...previewSections],
    sourceSectionIndex || 0,
    (targetSectionIndex || 0) - 1,
    moveType || "into"
  );

  // Debug logging to see what data is received

  // Early return if required data is not available
  if (!location.state || !location.state.node) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h2>Error: Required data not found</h2>
        <p>Please go back and try again.</p>
        <div style={{ marginTop: "20px", textAlign: "left", fontSize: "14px" }}>
          <h3>Debug Information:</h3>
          <p>
            <strong>Has location.state:</strong>{" "}
            {!!location.state ? "Yes" : "No"}
          </p>
          <p>
            <strong>Has node:</strong> {!!location.state?.node ? "Yes" : "No"}
          </p>
          <p>
            <strong>ElementType:</strong>{" "}
            {location.state?.elementType || "undefined"}
          </p>
          <p>
            <strong>Title:</strong> {location.state?.title || "undefined"}
          </p>
          <p>
            <strong>Available keys:</strong>{" "}
            {location.state ? Object.keys(location.state).join(", ") : "none"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ position: "relative" }}
      key={`copy-preview-${node ? getNodeId(node) : "no-node"}`}
    >
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                border: "4px solid #f3f3f3",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                animation: "spin 1s linear infinite",
                marginBottom: "10px",
              }}
            ></div>
            <div style={{ fontSize: "16px", color: "#333" }}>
              Copying{" "}
              {nodeType === "richText"
                ? "Rich Text"
                : (nodeType || "Element")
                    .replace(/([a-z])([A-Z])/g, "$1 $2")
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
              ...
            </div>
          </div>
        </div>
      )}

      <ProcedureMoveAppBar
        showBackButton={false}
        selectedView={selectedView}
        setSelectedView={handleViewChange}
        title="Please Confirm the Changes"
        buttons={[{ label: "Confirm", onClick: handleConfirm }]}
        onBack={() => navigate(-1)}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "8px 20px",
          flexWrap: "wrap", // for responsiveness
        }}
      >
        {/* Checkbox + Title + Description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              margin: 0,
            }}
          >
            <span style={{ fontSize: "19px", fontWeight: 600 }}>
              {toType || "Unknown"} {toPartNumber || "Unknown"}
            </span>
          </label>
        </div>

        {/* Right-aligned section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginLeft: "auto",
          }}
        >
          <div>
            <strong>Status: </strong>
            {/* <span style={{ color: "orange" }}>{status}</span> */}
            <span style={{ color: "orange" }}>
              {location.state?.status || status || "Unknown"}
            </span>
          </div>

          <div>
            <strong>Revision: </strong>
            <span>{revision || "Unknown"}</span>
          </div>
        </div>
      </div>
      <div style={{ padding: "20px" }}>
        <div style={{ marginTop: "20px" }}>
          {reorderedPreviewSections
            .sort((a, b) => {
              const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
              const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

              const orderCompare = compareSiblingOrders(orderA, orderB);
              if (orderCompare !== 0) return orderCompare;

              // Fallback: compare created_at
              const dateA = new Date(a.created_at);
              const dateB = new Date(b.created_at);
              return dateB - dateA; // latest comes first
            })
            .map((section, index) => {
              const stepsToRender =
                stepsBySection[section.section_tree_node_id] || [];
              return (
                <div
                  className="header-spacing"
                  key={section.section_tree_node_id}
                  style={{
                    margin: "20px 0",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <h2 style={{ margin: 0, fontSize: "20px" }}>
                      Section {index + 1}
                    </h2>
                  </label>
                  <hr />
                  <hr />
                  <div>{section.section_name}</div>
                  <div>{section.short_description}</div>

                  {(stepsToRender || []).map((step, stepIndex) => {
                    const stepId = step.step_tree_node_id;
                    const sectionId = section.section_tree_node_id;
                    stepCounter++;
                    const stepsBeforeThisSection = previewSections
                      .slice(0, index)
                      .reduce(
                        (acc, s) =>
                          acc +
                          (stepsBySection[s.section_tree_node_id]?.length || 0),
                        0
                      );
                    const globalStepNumber =
                      stepsBeforeThisSection + stepIndex + 1;

                    const substeps = substepsByStep[stepId] || [];

                    const substepLetter = String.fromCharCode(
                      65 + substeps.length
                    );

                    const currentAttachmentCounter = attachmentCounter;
                    const currentVideoCounter = videoCounter;
                    const currentFormCounter = formCounter;
                    const currentCanvasCounter = canvasCounter;
                    const currentRichTextCounter = richTextCounter;

                    richTextsCounter[stepId] = currentRichTextCounter;

                    // Build combined items based on selected view
                    let combinedItems = [];


                    if (selectedView === "original") {
                      // In "View Original", show only original content (hide pasted elements)
                      combinedItems = [
                        ...(richTexts[stepId] || []).map((item) => ({
                          ...item,
                          type: "richText",
                        })),
                        ...(forms[stepId] || []).map((item) => ({
                          ...item,
                          type: "form",
                        })),
                        ...(attachments[stepId] || []).map((item) => ({
                          ...item,
                          type: "attachment",
                        })),
                        ...(canvases[stepId] || []).map((item) => ({
                          ...item,
                          type: "canvas",
                        })),
                        ...(videos[stepId] || []).map((item) => ({
                          ...item,
                          type: "video",
                        })),
                      ];
                    } else {
                      // In "View New", include both existing content and pasted elements
                      // But avoid duplication by not adding from pastedElements again
                      combinedItems = [
                        ...(newRichTexts[stepId] || []).map((item) => ({
                          ...item,
                          type: "richText",
                        })),
                        ...(newForms[stepId] || []).map((item) => ({
                          ...item,
                          type: "form",
                        })),
                        ...(newAttachments[stepId] || []).map((item) => ({
                          ...item,
                          type: "attachment",
                        })),
                        ...(newCanvases[stepId] || []).map((item) => ({
                          ...item,
                          type: "canvas",
                        })),
                        ...(newVideos[stepId] || []).map((item) => ({
                          ...item,
                          type: "video",
                        })),
                      ];
                    }

                    // Add the node being copied to the target step (only if not already present)
                    if (
                      node &&
                      targetStepId === stepId &&
                      selectedView === "new"
                    ) {

                      // Check if the node is already in the combined items to avoid duplication
                      const isAlreadyPresent = combinedItems.some(
                        (item) =>
                          item.tree_node_id === node.tree_node_id ||
                          item.attachment_tree_node_id ===
                            node.attachment_tree_node_id ||
                          item.rich_text_tree_node_id ===
                            node.rich_text_tree_node_id
                      );

                      if (!isAlreadyPresent) {
                        if (nodeType === "attachment") {
                          const attachmentItem = {
                            ...node,
                            type: "attachment",
                            itemType: "attachment",
                            nodeType: "attachment",
                          };
                          combinedItems.push(attachmentItem);
                        } else if (nodeType === "richText") {
                          const richTextItem = {
                            ...node,
                            type: "richText",
                            itemType: "richText",
                            nodeType: "richText",
                          };
                          combinedItems.push(richTextItem);
                        }
                      } else {
                      }
                    }


                    const filteredItems = combinedItems.filter((item) => {
                      // Don't filter out the node being copied (it should always be shown)
                      if (node && item === node) {
                        return true;
                      }

                      // Don't filter out items that have the same tree_node_id as the node being copied
                      if (
                        node &&
                        node.tree_node_id &&
                        item.tree_node_id === node.tree_node_id
                      ) {
                        return true;
                      }

                      // Filter out items that belong to substeps (original logic)
                      return (
                        !item.substep_id_ref && !item.sub_step_tree_node_id
                      );
                    });


                    // Sort by sibling_order to maintain proper positioning
                    filteredItems.sort((a, b) => {
                      const orderA = parseInt(a.sibling_order || 0);
                      const orderB = parseInt(b.sibling_order || 0);
                      return orderA - orderB;
                    });

                    return (
                      <div
                        className="header-spacing"
                        key={stepId}
                        style={{ marginTop: "20px" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <label
                            style={{
                              display: "flex",
                              gap: "8px",
                              cursor: "pointer",
                            }}
                          >
                            <h2
                              style={{
                                margin: 0,
                                color: "#333",
                                fontSize: "20px",
                              }}
                            >
                              Step {globalStepNumber}
                            </h2>
                          </label>

                          <div
                            style={{
                              color: "#888",
                              fontSize: "14px",
                              fontWeight: "normal",
                            }}
                          >
                            {generateCompleteTreePath({
                              sectionIndex: index,
                              stepIndex: globalStepNumber - 1,
                              substepIndex: undefined,
                              nodeType: undefined,
                              nodeIndex: undefined,
                            })}
                          </div>
                        </div>
                        <hr />
                        <p style={{ whiteSpace: 'pre-wrap' }}>{step.short_description || ""}</p>
                        <p>
                          <p>
                            {structuredClone(step.step_details)
                              .sort(
                                (a, b) =>
                                  new Date(a.created_at) -
                                  new Date(b.created_at)
                              )
                              .map((myStep) => {
                                const partNumber =
                                  myStep.part_info?.part_number || "";
                                const description =
                                  myStep.part_info?.description || "";
                                const qtyText =
                                  myStep.qty && myStep.qty != 0
                                    ? `Qty ${myStep.qty}`
                                    : "";

                                const partText = partNumber
                                  ? ` (${partNumber})`
                                  : "";

                                return (
                                  <>
                                    {myStep.action}
                                    {partText}{" "}
                                    <RenderHtml content={description} />{" "}
                                    {qtyText}&nbsp;
                                  </>
                                );
                              })}
                          </p>
                        </p>

                        <h4>
                          {step.bom_table_title_info?.category_name || ""}
                        </h4>

                        <table className="table">
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>
                                {
                                  step.part_number_column_title_ref_info
                                    ?.category_name
                                }{" "}
                              </th>
                              <th>Description</th>
                              <th>Qty Per</th>
                              <th>Unit</th>
                            </tr>
                          </thead>

                          <tbody>
                            {step.parts_required.map((row) => (
                              <tr>
                                <td>{row.part_info.type_info.name}</td>
                                <td>{row.part_info.part_number}</td>
                                <td>
                                  <RenderHtml
                                    content={row.part_info.description}
                                  />
                                </td>
                                <td>{row.qty === 0 ? "" : row.qty}</td>
                                <td>{row.part_info?.unit_info?.unit_name}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {filteredItems.map((item) => {
                          // Don't filter out the node being copied during rendering
                          if (
                            node &&
                            (item === node ||
                              (node.tree_node_id &&
                                item.tree_node_id === node.tree_node_id))
                          ) {
                            // Allow the node being copied to render
                          } else if (
                            item.substep_id_ref ||
                            item.sub_step_tree_node_id
                          ) {
                            return null;
                          }
                          switch (item.type) {
                            case "richText": {
                              const globalRichTextNumber = richTextCounter++;
                              const label = (
                                <h2 style={{ color: "#333", fontSize: "20px" }}>
                                  Rich Text {globalRichTextNumber}
                                </h2>
                              );

                              return (
                                <div
                                  className="header-spacing"
                                  key={
                                    item.rich_text_tree_node_id ||
                                    item.rich_text_node_id
                                  }
                                  style={{ marginTop: "20px" }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {label}
                                    </label>

                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber}
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      marginLeft: "5px",
                                      marginTop: "-10px",
                                    }}
                                  >
                                    {item.short_description}
                                  </div>
                                  <div
                                    style={{
                                      marginLeft: "4px",
                                      marginTop: "5px",
                                    }}
                                  >
                                    {item.category_data?.category_name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      marginLeft: "4px",
                                      marginTop: "5px",
                                    }}
                                  >
                                    <div
                                      style={{ marginTop: "-6px" }}
                                      dangerouslySetInnerHTML={{
                                        __html: item.rich_text_content,
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            }
                            case "video": {
                              const globalVideoNumber = videoCounter++;
                              const label = (
                                <h2 style={{ color: "#333", fontSize: "20px" }}>
                                  Video {globalVideoNumber}
                                </h2>
                              );

                              return (
                                <div
                                  className="header-spacing"
                                  key={`rich-text-${getNodeId(item)}`}
                                  style={{ marginTop: "20px" }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {label}
                                    </label>

                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber}
                                    </div>
                                  </div>
                                  {renderVideo(item)}

                                  <div>{item.video_url}</div>

                                  <div>{item.short_description}</div>
                                </div>
                              );
                            }

                            case "form": {
                              const globalFormNumber = formCounter++;
                              const label = (
                                <h2 style={{ color: "#333", fontSize: "20px" }}>
                                  Form {globalFormNumber}
                                </h2>
                              );
                              return (
                                <div key={`form-${getNodeId(item)}`}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        cursor: "pointer",
                                        marginTop: "20px",
                                      }}
                                    >
                                      {label}
                                    </label>

                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber}
                                    </div>
                                  </div>
                                  <div>{item.short_description}</div>
                                </div>
                              );
                            }
                            case "canvas": {
                              const pages = getPages(item);

                              const globalCanvasNumber = canvasCounter++;
                              const label = (
                                <h2 style={{ color: "#333", fontSize: "20px" }}>
                                  Canvas {globalCanvasNumber}
                                </h2>
                              );
                              const shapes = item.shapes || [];

                              return (
                                <div
                                  className="header-spacing"
                                  key={`canvas-${getNodeId(item)}`}
                                  style={{ marginTop: "40px" }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <h2>{label}</h2>
                                    </label>

                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber}
                                    </div>
                                  </div>
                                  <div>{item.short_description}</div>

                                  {/* Display all pages vertically */}
                                  {pages && pages.length > 0 ? (
                                    <CanvasDisplay
                                      pages={pages}
                                      hasUserContent={hasUserContent}
                                      getTextStyle={getTextStyle}
                                      stripHtmlPreserveFormatting={
                                        stripHtmlPreserveFormatting
                                      }
                                      estimateKonvaTextHeight={
                                        estimateKonvaTextHeight
                                      }
                                      formatListContent={formatListContent}
                                      hasNoteContent={hasNoteContent}
                                      hasTaskContent={hasTaskContent}
                                      calculateGroupHeight={
                                        calculateGroupHeight
                                      }
                                      calculateTextBlockWidth={
                                        calculateTextBlockWidth
                                      }
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        padding: "20px",
                                        border: "1px dashed #ccc",
                                        textAlign: "center",
                                      }}
                                    >
                                      <p>Canvas content not available</p>
                                      <p>
                                        Debug info:{" "}
                                        {JSON.stringify({
                                          hasPages: !!pages,
                                          pagesLength: pages?.length,
                                          itemType: item.type,
                                          itemKeys: Object.keys(item),
                                        })}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            case "attachment": {
                              // Handle different attachment data structures
                              let attachmentList = [];
                              let attachmentData = null;


                              // Check for different possible data structures
                              if (
                                item.attachments &&
                                item.attachments.length > 0
                              ) {
                                attachmentList = item.attachments;
                                attachmentData = item;
                              } else if (
                                item.data &&
                                item.data.attachments &&
                                item.data.attachments.length > 0
                              ) {
                                attachmentList = item.data.attachments;
                                attachmentData = item.data;
                              } else if (item.attachment_tree_node_id) {
                                // Single attachment object
                                attachmentList = [item];
                                attachmentData = item;
                              } else if (
                                item.data &&
                                item.data.attachment_tree_node_id
                              ) {
                                // Single attachment object in data
                                attachmentList = [item.data];
                                attachmentData = item.data;
                              }


                              if (attachmentList.length === 0) return null;

                              const globalAttachmentNumber =
                                attachmentCounter++;
                              const isCopied = item.isCopied;
                              const label = (
                                <h2
                                  style={{
                                    color: isCopied ? "#007bff" : "#333",
                                    fontSize: "20px",
                                    border: isCopied
                                      ? "2px solid #007bff"
                                      : "none",
                                    padding: isCopied ? "5px 10px" : "0",
                                    borderRadius: isCopied ? "5px" : "0",
                                    backgroundColor: isCopied
                                      ? "#f0f8ff"
                                      : "transparent",
                                  }}
                                >
                                  {isCopied ? "📋 " : ""}Attachment{" "}
                                  {globalAttachmentNumber}
                                  {isCopied ? " (Copied)" : ""}
                                </h2>
                              );


                              return (
                                <div key={getNodeId(item)}>
                                  <div
                                    className="header-spacing"
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      border: isCopied
                                        ? "2px dashed #007bff"
                                        : "none",
                                      padding: isCopied ? "10px" : "0",
                                      borderRadius: isCopied ? "8px" : "0",
                                      backgroundColor: isCopied
                                        ? "#f8f9ff"
                                        : "transparent",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        cursor: "pointer",
                                        marginTop: "20px",
                                      }}
                                    >
                                      {label}
                                    </label>

                                    <div
                                      style={{
                                        color: isCopied ? "#007bff" : "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      {isCopied
                                        ? "📋 Copied Element"
                                        : `Section ${
                                            index + 1
                                          } > Step ${globalStepNumber}`}
                                    </div>
                                  </div>
                                  <div>
                                    {attachmentData?.short_description ||
                                      item.short_description}
                                  </div>
                                  {attachmentList.map((attachmentItem, i) => {

                                    // Check if this is a parent attachment object with nested attachments
                                    let actualAttachment = attachmentItem;
                                    if (
                                      attachmentItem.attachments &&
                                      attachmentItem.attachments.length > 0
                                    ) {
                                      // Use the first attachment from the nested array
                                      actualAttachment =
                                        attachmentItem.attachments[0];
                                    }

                                    return (
                                      <div
                                        key={i}
                                        style={{ marginLeft: "20px" }}
                                      >
                                        {renderPreview(actualAttachment)}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }
                            default:
                              return null;
                          }
                        })}

                        {(
                          substepsByStepPreview[stepId] ||
                          substepsByStep[stepId] ||
                          []
                        ).map((substep, substepIndex) => {
                          const substepId = substep.sub_step_tree_node_id;

                          const combinedItems =
                            selectedView === "original"
                              ? [
                                  ...(richTexts[substepId] || []).map(
                                    (item) => ({
                                      ...item,
                                      type: "richText",
                                    })
                                  ),
                                  ...(forms[substepId] || []).map((item) => ({
                                    ...item,
                                    type: "form",
                                  })),
                                  ...(attachments[substepId] || []).map(
                                    (item) => ({
                                      ...item,
                                      type: "attachment",
                                    })
                                  ),
                                  ...(canvases[substepId] || []).map(
                                    (item) => ({
                                      ...item,
                                      type: "canvas",
                                    })
                                  ),
                                  ...(videos[substepId] || []).map((item) => ({
                                    ...item,
                                    type: "video",
                                  })),
                                ]
                              : [
                                  ...(newRichTexts[substepId] || []).map(
                                    (item) => ({
                                      ...item,
                                      type: "richText",
                                    })
                                  ),
                                  ...(newForms[substepId] || []).map(
                                    (item) => ({
                                      ...item,
                                      type: "form",
                                    })
                                  ),
                                  ...(newAttachments[substepId] || []).map(
                                    (item) => ({
                                      ...item,
                                      type: "attachment",
                                    })
                                  ),
                                  ...(newCanvases[substepId] || []).map(
                                    (item) => ({
                                      ...item,
                                      type: "canvas",
                                    })
                                  ),
                                  ...(newVideos[substepId] || []).map(
                                    (item) => ({
                                      ...item,
                                      type: "video",
                                    })
                                  ),
                                ];

                          // Sort by sibling_order to maintain the correct sequence
                          combinedItems.sort((a, b) => {
                            const orderA = parseInt(a.sibling_order || 0);
                            const orderB = parseInt(b.sibling_order || 0);
                            return orderA - orderB;
                          });

                          const filteredItems = combinedItems;

                          const stepsBeforeThisSection = previewSections
                            .slice(0, index)
                            .reduce(
                              (acc, s) =>
                                acc +
                                (stepsBySection[s.section_tree_node_id]
                                  ?.length || 0),
                              0
                            );
                          const globalStepNumber =
                            stepsBeforeThisSection + stepIndex + 1;

                          const substepLetter = String.fromCharCode(
                            65 + substepIndex
                          );
                          const newSubstepLetter = String.fromCharCode(
                            65 + substepIndex + 1
                          );
                          const allChildrenUnderSubstep = [
                            ...(newRichTexts[substepId] || []),
                            ...(newForms[substepId] || []),
                            ...(newAttachments[substepId] || []),
                            ...(newCanvases[substepId] || []),
                            ...(newVideos[substepId] || []),
                          ];
                          const globalSubstepLabel = `Substep ${globalStepNumber}${substepLetter}`;

                          const globalRichTextNumber = richTextCounter;
                          const globalAttachmentNumber = attachmentCounter;
                          const globalVideoNumber = videoCounter;
                          const globalCanvasNumber = canvasCounter;
                          const globalFormNumber = formCounter;

                          return (
                            <div
                              className="header-spacing"
                              key={substepId}
                              style={{ marginTop: "20px" }}
                            >
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <label
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      width: "100%",
                                    }}
                                  >
                                    <h2
                                      style={{
                                        margin: 0,
                                        color: "#333",
                                        fontSize: "20px",
                                      }}
                                    >
                                      {globalSubstepLabel}
                                    </h2>
                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber} &gt; Substep{" "}
                                      {globalStepNumber}
                                      {substepLetter}
                                    </div>
                                  </label>
                                </div>

                                <div style={{ whiteSpace: 'pre-wrap' }}>{substep.short_description}</div>
                              </div>

                              {filteredItems.map((item) => {
                                switch (item.type) {
                                  case "richText": {
                                    const globalRichTextNumber =
                                      richTextCounter++;
                                    const label = (
                                      <h2
                                        style={{
                                          color: "#333",
                                          fontSize: "20px",
                                        }}
                                      >
                                        Rich Text {globalRichTextNumber}
                                      </h2>
                                    );

                                    return (
                                      <div
                                        className="header-spacing"
                                        key={
                                          item.rich_text_tree_node_id ||
                                          item.rich_text_node_id
                                        }
                                        style={{ marginTop: "20px" }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "8px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              color: "#888",
                                              fontSize: "14px",
                                              fontWeight: "normal",
                                            }}
                                          >
                                            Section {index + 1} &gt; Step{" "}
                                            {globalStepNumber} &gt; Substep{" "}
                                            {globalStepNumber}
                                            {substepLetter}
                                          </div>
                                        </div>
                                        <div
                                          style={{
                                            marginLeft: "5px",
                                            marginTop: "-10px",
                                          }}
                                        >
                                          {item.short_description}
                                        </div>
                                        <div
                                          style={{
                                            marginLeft: "4px",
                                            marginTop: "5px",
                                          }}
                                        >
                                          {item.category_data?.category_name}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            marginLeft: "4px",
                                            marginTop: "5px",
                                          }}
                                        >
                                          <div
                                            style={{ marginTop: "-6px" }}
                                            dangerouslySetInnerHTML={{
                                              __html: item.rich_text_content,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    );
                                  }
                                  case "video": {
                                    const globalVideoNumber = videoCounter++;
                                    const label = (
                                      <h2
                                        style={{
                                          color: "#333",
                                          fontSize: "20px",
                                        }}
                                      >
                                        Video {globalVideoNumber}
                                      </h2>
                                    );

                                    return (
                                      <div
                                        key={`video-${getNodeId(item)}`}
                                        style={{ marginTop: "20px" }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "8px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              color: "#888",
                                              fontSize: "14px",
                                              fontWeight: "normal",
                                            }}
                                          >
                                            Section {index + 1} &gt; Step{" "}
                                            {globalStepNumber} &gt; Substep{" "}
                                            {globalStepNumber}
                                            {substepLetter}
                                          </div>
                                        </div>

                                        {renderVideo(item)}

                                        <div>{item.video_url}</div>

                                        <div>{item.short_description}</div>
                                      </div>
                                    );
                                  }

                                  case "form": {
                                    const globalFormNumber = formCounter++;
                                    const label = (
                                      <h2
                                        style={{
                                          color: "#333",
                                          fontSize: "20px",
                                        }}
                                      >
                                        Form {globalFormNumber}
                                      </h2>
                                    );
                                    return (
                                      <div key={`form-${getNodeId(item)}`}>
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "8px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              color: "#888",
                                              fontSize: "14px",
                                              fontWeight: "normal",
                                            }}
                                          >
                                            Section {index + 1} &gt; Step{" "}
                                            {globalStepNumber} &gt; Substep{" "}
                                            {globalStepNumber}
                                            {substepLetter}
                                          </div>
                                        </div>
                                        <div>{item.short_description}</div>
                                      </div>
                                    );
                                  }
                                  case "canvas": {
                                    const pages = getPages(item);

                                    const globalCanvasNumber = canvasCounter++;
                                    const label = (
                                      <h3
                                        style={{
                                          color: "#333",
                                          fontSize: "20px",
                                        }}
                                      >
                                        Canvas {globalCanvasNumber}
                                      </h3>
                                    );
                                    const shapes = item.shapes || [];

                                    return (
                                      <div
                                        key={`canvas-${getNodeId(item)}`}
                                        style={{ marginTop: "40px" }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "8px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              color: "#888",
                                              fontSize: "14px",
                                              fontWeight: "normal",
                                            }}
                                          >
                                            Section {index + 1} &gt; Step{" "}
                                            {globalStepNumber} &gt; Substep{" "}
                                            {globalStepNumber}
                                            {substepLetter}
                                          </div>
                                        </div>

                                        <div>{item.short_description}</div>

                                        {/* Display all pages vertically */}
                                        {pages && pages.length > 0 ? (
                                          <CanvasDisplay
                                            pages={pages}
                                            hasUserContent={hasUserContent}
                                            getTextStyle={getTextStyle}
                                            stripHtmlPreserveFormatting={
                                              stripHtmlPreserveFormatting
                                            }
                                            estimateKonvaTextHeight={
                                              estimateKonvaTextHeight
                                            }
                                            formatListContent={
                                              formatListContent
                                            }
                                            hasNoteContent={hasNoteContent}
                                            hasTaskContent={hasTaskContent}
                                            calculateGroupHeight={
                                              calculateGroupHeight
                                            }
                                            calculateTextBlockWidth={
                                              calculateTextBlockWidth
                                            }
                                          />
                                        ) : (
                                          <div
                                            style={{
                                              padding: "20px",
                                              border: "1px dashed #ccc",
                                              textAlign: "center",
                                            }}
                                          >
                                            <p>Canvas content not available</p>
                                            <p>
                                              Debug info:{" "}
                                              {JSON.stringify({
                                                hasPages: !!pages,
                                                pagesLength: pages?.length,
                                                itemType: item.type,
                                                itemKeys: Object.keys(item),
                                              })}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                  case "attachment": {
                                    // Handle different attachment data structures
                                    let attachmentList = [];
                                    let attachmentData = null;


                                    // Check for different possible data structures
                                    if (
                                      item.attachments &&
                                      item.attachments.length > 0
                                    ) {
                                      attachmentList = item.attachments;
                                      attachmentData = item;
                                    } else if (
                                      item.data &&
                                      item.data.attachments &&
                                      item.data.attachments.length > 0
                                    ) {
                                      attachmentList = item.data.attachments;
                                      attachmentData = item.data;
                                    } else if (item.attachment_tree_node_id) {
                                      // Single attachment object
                                      attachmentList = [item];
                                      attachmentData = item;
                                    } else if (
                                      item.data &&
                                      item.data.attachment_tree_node_id
                                    ) {
                                      // Single attachment object in data
                                      attachmentList = [item.data];
                                      attachmentData = item.data;
                                    }


                                    if (attachmentList.length === 0)
                                      return null;

                                    const globalAttachmentNumber =
                                      attachmentCounter++;
                                    const label = (
                                      <h2
                                        style={{
                                          color: "#333",
                                          fontSize: "20px",
                                        }}
                                      >
                                        Attachment {globalAttachmentNumber}
                                      </h2>
                                    );

                                    return (
                                      <div key={getNodeId(item)}>
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                            }}
                                          >
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              color: "#888",
                                              fontSize: "14px",
                                              fontWeight: "normal",
                                            }}
                                          >
                                            Section {index + 1} &gt; Step{" "}
                                            {globalStepNumber} &gt; Substep{" "}
                                            {globalStepNumber}
                                            {substepLetter}
                                          </div>
                                        </div>
                                        <div>
                                          {attachmentData?.short_description ||
                                            item.short_description}
                                        </div>
                                        {attachmentList.map(
                                          (attachmentItem, i) => {

                                            // Check if this is a parent attachment object with nested attachments
                                            let actualAttachment =
                                              attachmentItem;
                                            if (
                                              attachmentItem.attachments &&
                                              attachmentItem.attachments
                                                .length > 0
                                            ) {
                                              // Use the first attachment from the nested array
                                              actualAttachment =
                                                attachmentItem.attachments[0];
                                            }

                                            return (
                                              <div
                                                key={i}
                                                style={{
                                                  marginLeft: "20px",
                                                }}
                                              >
                                                {renderPreview(
                                                  actualAttachment
                                                )}
                                              </div>
                                            );
                                          }
                                        )}
                                      </div>
                                    );
                                  }
                                  default:
                                    return null;
                                }
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </div>
      </div>

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content_addsection">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <h4
                style={{
                  marginLeft: "23px",
                  fontSize: "18px",
                  color: "#333333",
                }}
              >
                Confirm Copy of {title || "Element"}
              </h4>
              <button
                onClick={() => setModalOpen(false)}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "10px",
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#666",
                }}
                disabled={isLoading}
              >
                ✕
              </button>
            </div>

            <div
              style={{ padding: "24px", marginTop: "-28px", color: "#333333" }}
            >
              <h4 style={{ fontSize: "14px" }}>
                For {partNumber || "Unknown"} &gt; Rev {revision || "Unknown"}{" "}
                &gt; {treeNode || "Unknown"}
              </h4>

              <p style={{ fontSize: "14px", color: "#333333" }}>
                <strong>Editor:</strong>{" "}
                <span style={{ marginLeft: "10px" }}>
                  {localStorage.getItem("user_name")}
                </span>
              </p>

              <label
                className="modal-label"
                style={{ fontSize: "14px", color: "#333333" }}
              >
                Comment
                <span className="required" style={{ marginLeft: "-1px" }}>
                  *
                </span>
              </label>
              <textarea
                className="modal-textarea"
                placeholder="Enter your comments here..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                style={{
                  width: "100%",
                  minHeight: "100px",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
                disabled={isLoading}
              ></textarea>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                padding: "16px 24px",
                borderTop: "1px solid #e0e0e0",
                marginTop: "-10px",
                position: "relative",
              }}
            >
              {/* {isLoading && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    zIndex: 10,
                  }}
                >
                  <div
                    className="loader-spinner"
                    style={{
                      border: "4px solid #f3f3f3",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      animation: "spin 1s linear infinite",
                      marginRight: "10px",
                    }}
                  ></div>
                  <span>Processing...</span>
                </div>
              )} */}
              <button
                onClick={() => setModalOpen(false)}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#6c757d",
                  color: "#e9ecef",
                  cursor: isLoading ? "not-allowed" : "pointer",
                }}
                disabled={isLoading}
              >
                Close
              </button>
              <button
                onClick={handleConfirmSave}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #007bff",
                  borderRadius: "4px",
                  background: "#007bff",
                  color: "white",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  opacity: isLoading ? 0.7 : 1,
                }}
                disabled={isLoading}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ height: "100px" }}></div>
    </div>
  );
};

export default ProcedureChildCopyPreview;
